/**
 * Diagram preview — shows converted output with mermaid/xml toggle.
 * - Mermaid mode → rendered SVG with zoom/pan
 * - XML mode → draw.io viewer or styled code fallback
 */

import { store } from './state';

let previewEl: HTMLElement;
let mermaidInstance: typeof import('mermaid')['default'] | null = null;

// Zoom/pan state
let scale = 1;
let panX = 0;
let panY = 0;
let isPanning = false;
let panStartX = 0;
let panStartY = 0;

let zoomControlsEl: HTMLElement | null = null;
let zoomIndicatorEl: HTMLElement | null = null;

export function initPreview(): void {
  previewEl = document.getElementById('diagram-preview') as HTMLElement;
  if (!previewEl) return;

  zoomControlsEl = document.getElementById('zoom-controls');
  zoomIndicatorEl = document.getElementById('zoom-indicator');

  // Wheel to zoom
  previewEl.addEventListener('wheel', (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    scale = Math.min(Math.max(0.2, scale + delta), 5);
    applyTransform();
  }, { passive: false });

  // Mouse drag to pan
  previewEl.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;
    isPanning = true;
    panStartX = e.clientX - panX;
    panStartY = e.clientY - panY;
    previewEl.style.cursor = 'grabbing';
  });

  window.addEventListener('mousemove', (e) => {
    if (!isPanning) return;
    panX = e.clientX - panStartX;
    panY = e.clientY - panStartY;
    applyTransform();
  });

  window.addEventListener('mouseup', () => {
    if (isPanning) {
      isPanning = false;
      previewEl.style.cursor = '';
    }
  });

  // Zoom control buttons
  document.getElementById('zoom-in-btn')?.addEventListener('click', () => {
    scale = Math.min(5, scale + 0.25);
    applyTransform();
  });

  document.getElementById('zoom-fit-btn')?.addEventListener('click', () => {
    fitToView();
  });

  document.getElementById('zoom-reset-btn')?.addEventListener('click', () => {
    resetTransform();
    applyTransform();
  });
}

function applyTransform(): void {
  const content = previewEl.querySelector('.preview-content') as HTMLElement;
  if (content) {
    content.style.transform = `translate(${panX}px, ${panY}px) scale(${scale})`;
    content.style.transformOrigin = '0 0';
  }
  if (zoomIndicatorEl) {
    zoomIndicatorEl.textContent = Math.round(scale * 100) + '%';
  }
}

function resetTransform(): void {
  scale = 1;
  panX = 0;
  panY = 0;
  if (zoomIndicatorEl) {
    zoomIndicatorEl.textContent = '100%';
  }
}

function fitToView(): void {
  const content = previewEl.querySelector('.preview-content') as HTMLElement;
  if (!content) return;

  const svg = content.querySelector('svg');
  if (!svg) return;

  // Get SVG natural dimensions from viewBox or client size
  let svgW: number, svgH: number;
  const viewBox = svg.getAttribute('viewBox');
  if (viewBox) {
    const parts = viewBox.split(/[\s,]+/).map(Number);
    svgW = parts[2];
    svgH = parts[3];
  } else {
    svgW = svg.clientWidth || svg.getBoundingClientRect().width;
    svgH = svg.clientHeight || svg.getBoundingClientRect().height;
  }

  if (!svgW || !svgH) return;

  const containerW = previewEl.clientWidth;
  const containerH = previewEl.clientHeight;

  scale = Math.min(containerW / svgW, containerH / svgH, 1);
  panX = 0;
  panY = 0;
  applyTransform();
}

function syncZoomControlsVisibility(show: boolean): void {
  if (zoomControlsEl) {
    zoomControlsEl.classList.toggle('hidden', !show);
  }
}

function buildThemeVars(colors: { colors: string[]; opacity: number }) {
  const bw = {
    primaryColor: '#FFFFFF',
    primaryBorderColor: '#000000',
    primaryTextColor: '#000000',
    lineColor: '#000000',
    secondaryColor: '#FFFFFF',
    tertiaryColor: '#FFFFFF',
  };

  if (colors.colors.length === 0) return bw;

  const toRgba = (hex: string, opacity: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const o = colors.opacity;
  return {
    ...bw,
    primaryColor: toRgba(colors.colors[0], o),
    secondaryColor: colors.colors[1] ? toRgba(colors.colors[1], o) : '#FFFFFF',
    tertiaryColor: colors.colors[2] ? toRgba(colors.colors[2], o) : '#FFFFFF',
  };
}

async function getMermaid() {
  if (!mermaidInstance) {
    const mod = await import('mermaid');
    mermaidInstance = mod.default;
  }

  const { colors } = store.get();
  const vars = buildThemeVars(colors);

  mermaidInstance.initialize({
    startOnLoad: false,
    theme: 'base',
    themeVariables: vars,
  } as any);

  return mermaidInstance;
}

function showXmlCodeFallback(xml: string): void {
  const escaped = xml
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  previewEl.innerHTML = `<div class="xml-code-preview"><pre>${escaped}</pre></div>`;
}

/**
 * Derive the mermaid and XML texts from the current state.
 * - editorMode 'mermaid': input is mermaid, output is xml
 * - editorMode 'xml': input is xml, output is mermaid
 */
function getTexts(): { mermaidText: string; xmlText: string } {
  const state = store.get();
  if (state.editorMode === 'mermaid') {
    return { mermaidText: state.input, xmlText: state.output };
  } else {
    return { mermaidText: state.output, xmlText: state.input };
  }
}

export async function renderPreview(): Promise<void> {
  if (!previewEl) return;

  const state = store.get();
  const { mermaidText, xmlText } = getTexts();

  if (state.previewMode === 'mermaid') {
    syncZoomControlsVisibility(true);
    if (!mermaidText.trim()) {
      previewEl.innerHTML = '<p style="color:var(--text-secondary);padding:2rem;">No mermaid text to preview.</p>';
      return;
    }
    try {
      const mermaid = await getMermaid();
      const id = 'preview-' + Date.now();
      const { svg } = await mermaid.render(id, mermaidText);
      resetTransform();
      previewEl.innerHTML = `<div class="preview-content">${svg}</div>`;
    } catch (err) {
      previewEl.innerHTML = `<p style="color:#c00;padding:2rem;">Preview error: ${(err as Error).message}</p>`;
    }
  } else {
    syncZoomControlsVisibility(false);
    const xml = xmlText;
    if (!xml.trim()) {
      previewEl.innerHTML = '<p style="color:var(--text-secondary);padding:2rem;">No output yet. Enter code and it will be converted.</p>';
      return;
    }

    const GraphViewer = (window as any).GraphViewer;
    if (GraphViewer) {
      const config = JSON.stringify({
        highlight: '#0000ff',
        nav: true,
        resize: true,
        xml: xml,
      });
      previewEl.innerHTML = '';
      const container = document.createElement('div');
      container.className = 'mxgraph';
      container.setAttribute('data-mxgraph', config);
      container.style.width = '100%';
      container.style.visibility = 'hidden';
      previewEl.appendChild(container);
      try {
        GraphViewer.createViewerForElement(container);
        // Post-render: scale down tall diagrams to fit vertically
        requestAnimationFrame(() => {
          const contentH = container.scrollHeight;
          const contentW = container.scrollWidth;
          const availH = previewEl.clientHeight - 40; // 20px padding each side
          const availW = previewEl.clientWidth - 40;
          if (contentH > 0 && contentW > 0) {
            const scale = Math.min(availW / contentW, availH / contentH, 1);
            if (scale < 1) {
              (container.style as any).zoom = String(scale);
            }
          }
          container.style.visibility = '';
        });
      } catch (err) {
        console.warn('draw.io viewer error, falling back to code view:', err);
        showXmlCodeFallback(xml);
      }
    } else {
      showXmlCodeFallback(xml);
    }
  }
}
